import { BaseGQLService } from '../../../common/services/BaseGQLService';
import { FilterOperator, SorterSet } from '../../../common/types/gql-types';
import { CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { ScanPlatform } from '../enums/ScanPlatform';
import { ScanMapper } from '../mappers/ScanMapper';
import { Scan } from '../models/Scan';

const SCAN_FRAGMENT = `
id
name
status
endedAt
project {
  id
  name
}
startedAt
totalHosts
failedHosts
successfulHosts
`;

export const GET_SCANS_QUERY = `
query(
    $first: Int,
    $last: Int,
    $after: String,
    $before: String,
    $filters: ExpandedScansFilter,
    $sorters: [ScansSorter]
  ) {
    viewer {
      organization {
        scans(
          first: $first,
          after: $after,
          last: $last,
          before: $before,
          filters: $filters,
          sorters: $sorters
        ) {
          totalCount
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          edges {
            node {
              ${SCAN_FRAGMENT}
            }
          }
        }
      }
    }
  }
`;

export const GET_SCAN_QUERY = `
query ($scanId: ID!) {
  node(id: $scanId) {
    ... on Scan {
      ${SCAN_FRAGMENT}
    }
  }
}
`;

export const CREATE_CROWDSTRIKE_SCAN_QUERY = `
mutation($scanName: String!, $platforms: [Platform!]!, $projectId: ID!, $profileId: ID, $hostname: String, $lastSeenHours: Int, $workLoad: Int, $maxFilesPerSecond: Int, $maxFileSize: Int, $exclude: [String], $tags: [String]) {
    scanHostsViaCrowdStrike(input: {
        scanName: $scanName,
        platforms: $platforms,
        projectId: $projectId,
        profileId: $profileId,
        hostname: $hostname,
        lastSeenHours: $lastSeenHours,
        workLoad: $workLoad,
        maxFilesPerSecond: $maxFilesPerSecond,
        maxFileSize: $maxFileSize,
        exclude: $exclude,
        tags: $tags
    }) {
        scan {
            id
        }
    }
}
`;

export const ESTIMATE_CROWDSTRIKE_HOSTS_QUERY = `
mutation($platforms: [Platform!], $hostname: String, $lastSeenHours: Int) {
  queryHostsViaCrowdStrike(
    input: {
      platforms: $platforms,
      hostname: $hostname,
      lastSeenHours: $lastSeenHours
    }
  ) {
    numberOfHosts
  }
}
`;

export const STOP_CROWDSTRIKE_SCAN_QUERY = `
mutation($scanId: ID!) {
  cancelScan(
    input: {
      scanId: $scanId,
    }
  ) {
    scan {
      id
    }

  }
}
`;

const DEFAULT_SORTER_SET: SorterSet[] = [{ field: 'id', order: 'DESC' }];

export type CreateCrowdStrikeScanPayload = {
  name: string;
  platform: ScanPlatform;
  projectId: number;
  profileId: number | null;
  hostname: string | null;
  lastSeenHours: number | null;
  workLoad: number | null;
  maxFilesPerSecond: number | null;
  maxFileSize: number | null;
  exclude: string[];
  tags: string[];
};

export type EstimateCrowdStrikeHostsPayload = {
  platform: ScanPlatform;
  hostname: string | null;
  lastSeenHours: number | null;
};

export class ScansService extends BaseGQLService {
  private readonly scanMapper = new ScanMapper();

  async getList(cursor: CursorInfo, filters: FilterOperator | null, sorters: SorterSet[]): Promise<PaginationResult<Scan>> {
    return this.query(
      GET_SCANS_QUERY,
      {
        ...this.mapCursorInfo(cursor),
        filters,
        sorters: this.getSorters(sorters),
      },
      (data) => {
        const { edges, pageInfo, totalCount } = data.viewer.organization.scans;
        return {
          data: edges.map(({ node }: any) => this.scanMapper.mapGQLModel(node)),
          pageInfo,
          totalCount,
        };
      }
    );
  }
  async getScan(scanId: number): Promise<Scan> {
    return this.query(
      GET_SCAN_QUERY,
      {
        scanId: this.gqlIdEncoder.encode(ModelType.Scan, scanId),
      },
      (data) => {
        return this.scanMapper.mapGQLModel(data.node);
      }
    );
  }

  async createCrowdStrikeScan({
    name,
    platform,
    projectId,
    profileId,
    hostname,
    lastSeenHours,
    workLoad,
    maxFilesPerSecond,
    maxFileSize,
    exclude,
    tags,
  }: CreateCrowdStrikeScanPayload): Promise<number> {
    return this.mutate(
      CREATE_CROWDSTRIKE_SCAN_QUERY,
      {
        scanName: name,
        platforms: [platform],
        projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId),
        profileId: profileId !== null ? this.gqlIdEncoder.encode(ModelType.Profile, profileId) : null,
        hostname,
        lastSeenHours,
        workLoad,
        maxFilesPerSecond,
        maxFileSize,
        exclude,
        tags,
      },
      (data) => this.gqlIdEncoder.decode(data.scanHostsViaCrowdStrike.scan.id)
    );
  }

  async estimateCrowdStrikeHosts({ platform, hostname, lastSeenHours }: EstimateCrowdStrikeHostsPayload): Promise<number> {
    return this.mutate(
      ESTIMATE_CROWDSTRIKE_HOSTS_QUERY,
      {
        platforms: [platform],
        hostname,
        lastSeenHours,
      },
      (data) => data.queryHostsViaCrowdStrike.numberOfHosts
    );
  }

  async stopScan(id: number): Promise<number> {
    return this.mutate(
      STOP_CROWDSTRIKE_SCAN_QUERY,
      {
        scanId: this.gqlIdEncoder.encode(ModelType.Scan, id),
      },
      (data) => this.gqlIdEncoder.decode(data.cancelScan.scan.id)
    );
  }

  /**
   * Scans are returned by the API sorted by ascending ID. IDs are sequential integers,
   * so this is practically equivalent to oldest-to-newest.
   * Unless explicit sorters are requested, invert the default sorting to show newest scans first.
   *
   * @param sorters the requested sorters
   * @returns the requested sorters if any, otherwise the default sorter
   */
  private getSorters(sorters: SorterSet[]): SorterSet[] {
    if (sorters.length > 0) {
      return sorters;
    }
    return DEFAULT_SORTER_SET;
  }
}
